import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Heart,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Download,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { sendQLEApproval } from '@/components/email/sendEmail';
import { isFeatureEnabled } from '@/components/utils/featureFlags';

export default function QualifyingLifeEvents() {
  const [qles, setQles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(true);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    checkFeature();
    init();
  }, []);

  const checkFeature = async () => {
    const enabled = await isFeatureEnabled('qle_enabled');
    setIsEnabled(enabled);
  };

  const init = async () => {
    setLoading(true);
    try {
      const user = await api.auth.me();
      const systemAdmin = user?.user_role === 'admin' || user?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      const employersData = await api.entities.Employer.list();
      setEmployers(employersData);

      if (systemAdmin) {
        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);

        if (defaultEmployerId) {
          const [qlesData, employeesData] = await Promise.all([
            api.entities.QualifyingLifeEvent.filter({ employee: { employer_id: defaultEmployerId } }),
            api.entities.Employee.filter({ employer_id: defaultEmployerId })
          ]);
          setQles(qlesData);
          setEmployees(employeesData);
        } else {
          setQles([]);
          setEmployees([]);
        }
      } else {
        const [qlesData, employeesData] = await Promise.all([
          api.entities.QualifyingLifeEvent.list('-created_at'),
          api.entities.Employee.list()
        ]);
        setQles(qlesData);
        setEmployees(employeesData);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      const [qlesData, employeesData] = await Promise.all([
        api.entities.QualifyingLifeEvent.filter({ employee: { employer_id: value } }),
        api.entities.Employee.filter({ employer_id: value })
      ]);
      setQles(qlesData);
      setEmployees(employeesData);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load qualifying life events');
    } finally {
      setLoading(false);
    }
  };

  const getEmployee = (employeeId) => {
    return employees.find(e => e.id === employeeId);
  };

  const getEmployer = (employerId) => {
    return employers.find(e => e.id === employerId);
  };

  const openReviewModal = (qle) => {
    setReviewModal(qle);
    setReviewNotes(qle.notes || '');
    setRejectionReason(qle.rejection_reason || '');
  };

  const handleApprove = async () => {
    if (!reviewModal) return;

    try {
      const user = await api.auth.me();
      const eventDate = new Date(reviewModal.event_date);
      const specialEnrollmentStart = new Date(eventDate);
      const specialEnrollmentEnd = new Date(eventDate);
      specialEnrollmentEnd.setDate(specialEnrollmentEnd.getDate() + 60);

      await api.entities.QualifyingLifeEvent.update(reviewModal.id, {
        status: 'approved',
        reviewed_by: user.email,
        reviewed_date: new Date().toISOString(),
        notes: reviewNotes,
        special_enrollment_start: specialEnrollmentStart.toISOString().split('T')[0],
        special_enrollment_end: specialEnrollmentEnd.toISOString().split('T')[0]
      });

      await api.entities.AuditLog.create({
        entity_type: 'QualifyingLifeEvent',
        entity_id: reviewModal.id,
        action: 'update',
        performed_by: user.email,
        notes: `Approved QLE: ${reviewModal.event_type}`
      });

      // Send approval email
      const employee = getEmployee(reviewModal.employee_id);
      if (employee) {
        const emailData = sendQLEApproval(employee.user_email, {
          employeeName: `${employee.first_name} ${employee.last_name}`,
          eventType: getEventTypeLabel(reviewModal.event_type),
          eventDate: format(new Date(reviewModal.event_date), 'MMMM d, yyyy'),
          enrollmentStart: format(specialEnrollmentStart, 'MMMM d, yyyy'),
          enrollmentEnd: format(specialEnrollmentEnd, 'MMMM d, yyyy'),
          enrollmentUrl: globalThis.location.origin + '/BenefitsCatalog'
        });

        await api.integrations.Core.SendEmail({
          to: emailData.to,
          subject: emailData.subject,
          body: emailData.html
        });
      }

      toast.success('QLE approved - employee notified');
      setReviewModal(null);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to approve QLE');
    }
  };

  const handleReject = async () => {
    if (!reviewModal || !rejectionReason) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const user = await api.auth.me();
      await api.entities.QualifyingLifeEvent.update(reviewModal.id, {
        status: 'rejected',
        reviewed_by: user.email,
        reviewed_date: new Date().toISOString(),
        rejection_reason: rejectionReason,
        notes: reviewNotes
      });

      await api.entities.AuditLog.create({
        entity_type: 'QualifyingLifeEvent',
        entity_id: reviewModal.id,
        action: 'update',
        performed_by: user.email,
        notes: `Rejected QLE: ${reviewModal.event_type} - ${rejectionReason}`
      });

      toast.success('QLE rejected');
      setReviewModal(null);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to reject QLE');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'pending_review': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getEventTypeLabel = (type) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md w-full">
          <CardContent className="p-12 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold">QLE Feature Disabled</h2>
            <p className="text-muted-foreground">
              The Qualifying Life Events feature is currently disabled by the system administrator.
              Mid-year enrollment changes are not available at this time.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Qualifying Life Events</h1>
        <p className="text-slate-500 mt-1">
          Review and approve mid-year enrollment requests
        </p>
      </div>

      {isSystemAdmin && (
        <Card>
          <CardContent className="p-4">
            <div className="grid gap-2">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((employer) => (
                    <SelectItem key={employer.id} value={employer.id}>
                      {employer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending Review</p>
                <p className="text-2xl font-bold text-slate-900">
                  {qles.filter(q => q.status === 'pending_review').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Approved</p>
                <p className="text-2xl font-bold text-slate-900">
                  {qles.filter(q => q.status === 'approved').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Rejected</p>
                <p className="text-2xl font-bold text-slate-900">
                  {qles.filter(q => q.status === 'rejected').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All QLE Requests</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Employer</TableHead>
                <TableHead>Event Type</TableHead>
                <TableHead>Event Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {qles.map((qle) => {
                const employee = getEmployee(qle.employee_id);
                const employer = getEmployer(qle.employer_id);
                return (
                  <TableRow key={qle.id}>
                    <TableCell>
                      {employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown'}
                    </TableCell>
                    <TableCell>{employer?.company_name || 'Unknown'}</TableCell>
                    <TableCell>{getEventTypeLabel(qle.event_type)}</TableCell>
                    <TableCell>
                      {format(new Date(qle.event_date), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(qle.status)}>
                        {qle.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {format(new Date(qle.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openReviewModal(qle)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {qles.length === 0 && (
            <div className="text-center py-12">
              <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No qualifying life event requests yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!reviewModal} onOpenChange={() => setReviewModal(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Qualifying Life Event</DialogTitle>
          </DialogHeader>
          
          {reviewModal && (
            <div className="space-y-6 py-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-700">Employee</p>
                  <p className="text-slate-900">
                    {(() => {
                      const emp = getEmployee(reviewModal.employee_id);
                      return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
                    })()}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-slate-700">Event Type</p>
                  <p className="text-slate-900">{getEventTypeLabel(reviewModal.event_type)}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-slate-700">Event Date</p>
                  <p className="text-slate-900">{format(new Date(reviewModal.event_date), 'MMM d, yyyy')}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-slate-700">Current Status</p>
                  <Badge className={getStatusColor(reviewModal.status)}>
                    {reviewModal.status.replace('_', ' ')}
                  </Badge>
                </div>
              </div>

              {reviewModal.description && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Description</p>
                  <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{reviewModal.description}</p>
                </div>
              )}

              {reviewModal.documentation && reviewModal.documentation.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Supporting Documents</p>
                  <div className="space-y-2">
                    {reviewModal.documentation.map((doc) => (
                      <a
                        key={doc.file_url}
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <FileText className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-700">{doc.document_type || 'Document'}</span>
                        <Download className="w-4 h-4 text-slate-400 ml-auto" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Review Notes</p>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about this review..."
                  rows={3}
                />
              </div>

              {reviewModal.status === 'pending_review' && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Rejection Reason (if rejecting)</p>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Provide reason if rejecting this request..."
                    rows={2}
                  />
                </div>
              )}

              {reviewModal.reviewed_by && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    Reviewed by {reviewModal.reviewed_by} on {format(new Date(reviewModal.reviewed_date), 'MMM d, yyyy')}
                  </p>
                  {reviewModal.rejection_reason && (
                    <p className="text-sm text-red-600 mt-2">
                      Rejection reason: {reviewModal.rejection_reason}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setReviewModal(null)}>
              Close
            </Button>
            {reviewModal?.status === 'pending_review' && (
              <>
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:bg-red-50"
                  onClick={handleReject}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button 
                  onClick={handleApprove}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}